import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Workspace } from "@/types/workspaces";
import { format } from "date-fns";
import { useState } from "react";
import { Line } from "react-chartjs-2";
import { DateRange } from "react-day-picker";
import { useActiveOrgWorkspaces } from "../hooks/organizations";
import { useUsageMetricsData } from "../hooks/useUsageMetricsData";
import { useUsageMetric } from "../queries/usage/useUsageMetric";
import type { UsageOperation } from "../queries/usage/useUsageOperations";
import { DatePickerWithRange } from "./DatePickerWithRange";

const labelMap = {
  log_request: "Requests",
  run_cell: "Eval Cells",
  run_node: "Workflow Node Execution",
  get_template: "Prompt Template Retrivals",
  run_group: "Playground Runs",
};

const lineOptions = {
  responsive: true,
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: "index" as const,
      intersect: false,
      callbacks: {
        label: (context: any) => {
          return context.parsed.y?.toLocaleString();
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#6b7280" },
    },
    y: {
      beginAtZero: true,
      grid: { color: "#e5e7eb" },
      ticks: { color: "#6b7280" },
    },
  },
};

const UsageMetricCard = ({
  op,
  date,
  organizationId,
  workspaceIds,
}: {
  op: UsageOperation;
  date: DateRange | undefined;
  organizationId: number | undefined;
  workspaceIds: string[];
}) => {
  const { data: metric, error } = useUsageMetric({
    operationId: op.id,
    date,
    organizationId,
    workspaceIds,
  });

  const label = labelMap[op.name as keyof typeof labelMap] || op.name;

  if (error) {
    return (
      <div className="py-8 text-center text-red-500">
        Error loading {label} usage: {String(error)}
      </div>
    );
  }

  const formattedLabels = metric?.labels.map((label) => {
    try {
      return format(new Date(label), "M/d");
    } catch {
      return label;
    }
  });

  const percentIncluded = Math.min(
    (metric?.included || 0 / (metric?.used || 1)) * 100,
    100,
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-lg font-semibold">{label}</div>
      </div>
      <div className="relative mb-4 w-full rounded">
        <div
          className="absolute rounded bg-blue-400"
          style={{ width: `${percentIncluded}%` }}
        ></div>
      </div>
      <div className="mb-2 w-full">
        <div style={{ width: "100%", height: 220 }}>
          <Line
            data={{
              labels: formattedLabels,
              datasets: [
                {
                  label: label,
                  data: metric?.data,
                  fill: false,
                  borderColor: "#3b82f6",
                  backgroundColor: "#3b82f6",
                  tension: 0.3,
                  pointRadius: 2,
                  pointBackgroundColor: "#3b82f6",
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              ...lineOptions,
              maintainAspectRatio: false,
              plugins: {
                ...lineOptions.plugins,
                datalabels: { display: false },
                tooltip: {
                  ...lineOptions.plugins.tooltip,
                  displayColors: false,
                  bodyFont: { size: 16 },
                  titleFont: { size: 14 },
                  enabled: false, // disable the default tooltip
                  external: function (context) {
                    // Custom tooltip implementation
                    // Tooltip Element
                    let tooltipEl = document.getElementById(
                      "custom-usage-tooltip",
                    );
                    if (!tooltipEl) {
                      tooltipEl = document.createElement("div");
                      tooltipEl.id = "custom-usage-tooltip";
                      tooltipEl.style.position = "absolute";
                      tooltipEl.style.pointerEvents = "none";
                      tooltipEl.style.zIndex = "1000";
                      document.body.appendChild(tooltipEl);
                    }

                    // Hide if no tooltip
                    const tooltipModel = context.tooltip;
                    if (tooltipModel.opacity === 0) {
                      tooltipEl.style.opacity = "0";
                      return;
                    }

                    // Set caret Position
                    tooltipEl.classList.remove(
                      "above",
                      "below",
                      "no-transform",
                    );
                    if (tooltipModel.yAlign) {
                      tooltipEl.classList.add(tooltipModel.yAlign);
                    } else {
                      tooltipEl.classList.add("no-transform");
                    }

                    // Set Text
                    if (tooltipModel.body) {
                      const label =
                        tooltipModel.dataPoints?.[0]?.dataset?.label || "";
                      const value =
                        tooltipModel.dataPoints?.[0]?.formattedValue || "";
                      const afterLabel = (() => {
                        if (metric && typeof metric.included === "number") {
                          return `${label} : ${metric.included.toLocaleString()} included`;
                        }
                        return "";
                      })();

                      tooltipEl.innerHTML = `
                        <div style="background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); padding: 12px 16px; min-width: 120px;">
                          <div style="font-weight: 600; font-size: 13px; margin-bottom: 1px;">
                            ${tooltipModel.dataPoints?.[0]?.label || ""}
                          </div>
                          <div style="color: #2563eb; font-weight: 600; font-size: 13px; margin-bottom: 1px;">
                            ${label} : ${value}
                          </div>
                          <div style="color: #9ca3af; font-size: 13px; margin-top: 1px;">
                            ${afterLabel}
                          </div>
                        </div>
                      `;
                    }

                    const { chart } = context;
                    const position = chart.canvas.getBoundingClientRect();

                    tooltipEl.style.opacity = "1";
                    tooltipEl.style.left =
                      position.left +
                      window.pageXOffset +
                      tooltipModel.caretX +
                      "px";
                    tooltipEl.style.top =
                      position.top +
                      window.pageYOffset +
                      tooltipModel.caretY +
                      "px";
                  },
                },
              },
              scales: {
                ...lineOptions.scales,
                x: {
                  ...lineOptions.scales.x,
                  ticks: { ...lineOptions.scales.x.ticks, font: { size: 12 } },
                },
                y: {
                  ...lineOptions.scales.y,
                  ticks: {
                    ...lineOptions.scales.y.ticks,
                    font: { size: 12 },
                    callback: function (tickValue: string | number) {
                      if (typeof tickValue === "number") {
                        return tickValue.toLocaleString();
                      }
                      return tickValue;
                    },
                  },
                  suggestedMax: Math.max(...(metric?.data || [])) * 1.2,
                },
              },
            }}
            height={220}
          />
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {/* Plan note placeholder */}
        </div>
      </div>
    </div>
  );
};

const UsageMetrics = () => {
  const workspaces = useActiveOrgWorkspaces();
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);

  const handleWorkspaceToggle = (id: string) => {
    setSelectedWorkspaces((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id],
    );
  };

  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 14)),
    to: new Date(),
  });

  const { operations, error: opsError, organizationId } = useUsageMetricsData();

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <DatePickerWithRange date={date} setDate={setDate} />
        </div>
        <div className="flex h-8 min-w-[156px]">
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full">
              {selectedWorkspaces.length === 0
                ? "All Workspaces"
                : selectedWorkspaces.length === 1
                ? workspaces.find(
                    (w: Workspace) => w.id.toString() === selectedWorkspaces[0],
                  )?.name
                : `${selectedWorkspaces.length} selected`}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {workspaces.map((ws: Workspace) => (
                <DropdownMenuItem
                  key={ws.id}
                  onSelect={() => handleWorkspaceToggle(ws.id.toString())}
                  className="flex items-center"
                >
                  <input
                    type="checkbox"
                    checked={selectedWorkspaces.includes(ws.id.toString())}
                    className="mr-2"
                  />
                  {ws.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {Boolean(opsError) && (
        <div className="py-8 text-center text-red-500">{String(opsError)}</div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {operations.map((op: UsageOperation) => (
          <UsageMetricCard
            key={op.id}
            op={op}
            date={date}
            organizationId={organizationId}
            workspaceIds={selectedWorkspaces}
          />
        ))}
      </div>
    </div>
  );
};

export default UsageMetrics;
